import { z } from 'zod';

import { IsoDate, Page, PageQuery, Role, Uid, Ulid } from '../primitives';
import { DateRangeQuery } from './common';

/** An `audit_log` row (append-only). */
export const AuditEntryDTO = z.object({
  id: Ulid,
  /** Null for rows written outside a request (cron, migrations); every CMS route has an actor. */
  actorUserId: Uid.nullable(),
  actorEmail: z.string().nullable(),
  actorRole: Role.nullable(),
  /** e.g. `song.update`, `home.publish`, `grant.redeem`, `upload.create`. */
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  /**
   * A human title for `entityId` — the song's title, the artist's or playlist's name, the
   * user's name or e-mail, the grant's match value, the session's join code — resolved
   * server-side when the entity still exists.
   *
   * QA 2026-09-15: the console's ENTIDAD column read `song 01M2PZSBAYZ007FG40DVQ7ZHCN`, which
   * is not something an operator can recognise, and the row's accessible name repeated it. Null
   * for a kind nobody resolves, for a bulk pseudo-id (`import:0`) and for a row whose entity has
   * since been deleted; the console falls back to the raw id in that case.
   */
  entityLabel: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  reqId: z.string().nullable(),
  createdAt: IsoDate,
});
export type AuditEntryDTO = z.infer<typeof AuditEntryDTO>;

/** `GET /cms/audit?actor=&entity=&from=&to=` */
export const CmsAuditQuery = PageQuery.merge(DateRangeQuery).extend({
  actor: Uid.optional(),
  entity: z.string().max(64).optional(),
  entityId: z.string().max(128).optional(),
  action: z.string().max(64).optional(),
});
export type CmsAuditQuery = z.infer<typeof CmsAuditQuery>;
export type CmsAuditQueryInput = z.input<typeof CmsAuditQuery>;

export const CmsAuditResponse = Page(AuditEntryDTO);
export type CmsAuditResponse = z.infer<typeof CmsAuditResponse>;
